import { useContext, useEffect } from 'react'
import { createGuestPaymentOrderRequest, createOneClickPaymentOrderRequest } from '../../../helpers/pinflagRequest'
import { usePayment } from '../usePayment/usePayment'
import { DataContext } from '../../../context/DataContext'
import { parsePaymentData } from '../../../helpers/payment/paymentOrderParsers'
import { KLAP } from '../../../constants/payment'
import { usePaymentOrderHandlers } from '../usePaymentOrderHandlers'
import { isCardNumberValid, isCvvValid, isExpiryDateValid } from '../../../helpers/payment/cardDataHelpers'

export const useKlap = () => {
  const {
    setIsLoadingGatewayForm,
    setPaymentOrderResult,
    setPaymentError,
    selectedPaymentCard,
    installments,
    selectedPaymentOption,
    forceNewPayment,
    setForceNewPayment,
    isLoadingGatewayForm,
    gateway,
    paymentOrderResult,
    cardNumber,
    cardExpiryDate,
    cardCvv,
    setShowGatewayForm,
    userToken,
    setIsPaying,
    setHasConfirmedPaymentMethod,
    resetCardData
  } = usePayment()

  const {
    pollPaymentOrderStatus
  } = usePaymentOrderHandlers()

  const { userInfo, addressInfo, cartData, ecommerceInfo, setShowFinalLoader } =
    useContext(DataContext)

  const data = {
    userInfo,
    addressInfo,
    cartData,
    ecommerceInfo,
    installments,
    selectedPaymentCard,
    selectedPaymentOption
  }

  const parseExtraDataKlap = (data) => {
    return {
      forceNewPayment
    }
  }

  const payWithKlap = () => {
    return window.KLAP.payOrder()
  }

  const handlePaymentApproval = async () => {
    setShowFinalLoader(true)
    return pollPaymentOrderStatus({ orderId: paymentOrderResult?.orderId, gateway: KLAP, paymentMethod: selectedPaymentOption })
  }

  const handlePaymentRejected = (error) => {
    console.error('❗El pago fue rechazado:', error)
    setIsPaying(false)
    setShowFinalLoader(false)
    setShowGatewayForm(false)
    setHasConfirmedPaymentMethod(false)
    resetCardData()
    setPaymentError('El pago fue rechazado, intenta nuevamente')
  }

  const canUserPayWithKlap = () => {
    const cardNumberIsValid = isCardNumberValid(cardNumber)
    const expiryDateIsValid = isExpiryDateValid(cardExpiryDate)
    const cvvIsValid = isCvvValid(cardCvv)
    if (cardNumberIsValid && expiryDateIsValid && cvvIsValid) return true
    return false
  }

  const payOneClickWithKlap = async () => {
    const paymentOrderData = parsePaymentData({
      data,
      gateway: KLAP,
      useOneClick: true,
      forceNewPayment
    })
    const extraData = parseExtraDataKlap(data)
    try {
      const response = await createOneClickPaymentOrderRequest(
        { ...paymentOrderData, ...extraData },
        userToken.current
      )
      setPaymentOrderResult(response)
      return pollPaymentOrderStatus({
        orderId: response.orderId,
        gateway: KLAP,
        paymentMethod: response.paymentMethod === 'debitCard' ? 'debit' : 'credit'
      })
    } catch (error) {
      console.error('❗Error al crear la orden de pago:', error)
      setPaymentError('Ocurrió un error al crear la orden de compra')
      setShowFinalLoader(false)
    } finally {
      setIsPaying(false)
    }
  }

  const confirmPaymentMethodWithKlap = async (selectedPaymentMethod) => {
    // Show gateway form
    setShowGatewayForm(true)
    setIsLoadingGatewayForm(true)
    const paymentOrderData = parsePaymentData({
      data: { ...data, selectedPaymentOption: selectedPaymentMethod },
      gateway: KLAP,
      useOneClick: false,
      forceNewPayment
    })
    const extraData = parseExtraDataKlap(data)
    try {
      const response = await createGuestPaymentOrderRequest({
        ...paymentOrderData,
        ...extraData
      })
      setPaymentOrderResult(response)
      setForceNewPayment(false)
      return response
    } catch (error) {
      console.error('❗Error al crear la orden de pago:', error)
      setPaymentError('Ocurrió un error al crear la orden de pago')
    } finally {
      setIsLoadingGatewayForm(false)
    }
  }

  useEffect(() => {
    if (isLoadingGatewayForm || gateway !== KLAP) return
    const initializeKlap = async () => {
      try {
        await window.KLAP.init({
          method: 'tarjetas',
          debug: true
        })
      } catch (error) {
        console.error('🔴🔴Error initializing KLAP', error)
      }
    }

    if (window.KLAP) {
      initializeKlap()
    } else {
      console.error('🔴🔴KLAP is not defined')
    }
  }, [isLoadingGatewayForm])

  return {
    confirmPaymentMethodWithKlap,
    payWithKlap,
    payOneClickWithKlap,
    handlePaymentApproval,
    handlePaymentRejected,
    canUserPayWithKlap
  }
}
